import { ErrorMessage, Field, Form, Formik } from "formik";
import { Modal } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import axios from "axios";
import { toast } from "react-toastify";
import { registerAccount, registerUser } from "../../features/user/userSlice";
import { base_url } from "../../utils/axiosConfig";

const MemberSignUp = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(0);
  const [isRegistered, setIsRegistered] = useState(false);
  const [packages, setPackages] = useState([]);
  const [referredUserData, setReferredUserData] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const timeoutRef = useRef(null);
  const [useSameAddress, setUseSameAddress] = useState(false);

  // Debounce function for referral code validation
  const debounce = (func, delay) => {
    return (...args) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => func(...args), delay);
    };
  };

  // Fetch referred user data
  const fetchReferredUserData = async (referralCode) => {
    if (!referralCode) {
      setReferredUserData(null);
      return;
    }
    try {
      const response = await axios.post(
        `${base_url}v1/user/verifyReferralCode`,
        {
          referralCode,
        },
      );
      setReferredUserData(response.data.data);
    } catch (error) {
      console.error("Error fetching referred user data:", error);
      setReferredUserData(null);
    }
  };

  const debouncedFetchReferredUserData = debounce(fetchReferredUserData, 500);

  // Fetch packages for the commented-out package selection step
  useEffect(() => {
    if (currentStep === 3 && show) {
      axios
        .get(`${base_url}v1/package/fetchAllpackage`)
        .then((response) => {
          setPackages(response.data.data);
        })
        .catch((error) => {
          console.error("Error fetching packages:", error);
        });
    }
  }, [currentStep, show]);

  // Cleanup debounce timeout
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Calculate max date for 18+ validation (today minus 18 years)
  const maxDate18 = new Date();
  maxDate18.setFullYear(maxDate18.getFullYear() - 18);

  // Validation schemas for each step
  const signupSchemas = [
    Yup.object({
      name: Yup.string().required("Full Name is required"),
      dob: Yup.date()
        .required("DOB is required")
        .max(maxDate18, "You must be 18+ years of age"),
      gender: Yup.string().required("Gender is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      mobile: Yup.string()
        .matches(/^[0-9]+$/, "Not a Valid Phone Number")
        .required("Phone is required"),
      bloodGroup: Yup.string().required("Blood Group is required"),
      profilePhoto: Yup.mixed().required("Profile photo is required"),
      resAddress_line: Yup.string().required("Residential Address is required"),
      resCountry: Yup.string().required("Residential Country is required"),
      resState: Yup.string().required("Residential State is required"),
      resCity: Yup.string().required("Residential City is required"),
      resDistrict: Yup.string().required("Residential District is required"),
      resPincode: Yup.string().required("Residential Pincode is required"),
      correAddress_line: Yup.string().required(
        "Correspondence/Office Address is required",
      ),
      correCountry: Yup.string().required(
        "Correspondence/Office Country is required",
      ),
      correState: Yup.string().required(
        "Correspondence/Office State is required",
      ),
      correCity: Yup.string().required(
        "Correspondence/Office City is required",
      ),
      correDistrict: Yup.string().required(
        "Correspondence/Office District is required",
      ),
      correPincode: Yup.string().required(
        "Correspondence/Office Pincode is required",
      ),
    }),
    Yup.object({
      referralCode: Yup.string(),
      termsAccepted: Yup.boolean().oneOf(
        [true],
        "You must accept the Terms and Conditions",
      ),
      declarationAccepted: Yup.boolean().oneOf(
        [true],
        "You must accept the Declaration",
      ),
    }),
  ];

  const initialSignupValues = {
    name: "",
    dob: "",
    gender: "",
    email: "",
    mobile: "",
    bloodGroup: "",
    profilePhoto: null,
    resAddress_line: "",
    resCountry: "",
    resState: "",
    resCity: "",
    resDistrict: "",
    resPincode: "",
    correAddress_line: "",
    correCountry: "",
    correState: "",
    correCity: "",
    correDistrict: "",
    correPincode: "",
    referralCode: "",
    termsAccepted: false,
    declarationAccepted: false,
  };

  const steps = [
    "Personal Information",
    "Bank Details",
    "KYC Documents",
    "Reference",
  ];

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      dialogClassName="modal-90w md:modal-lg"
    >
      <Modal.Body className="p-4 md:p-6">
        <div className="relative bg-white rounded-lg">
          <button
            onClick={onHide}
            className="absolute top-2 right-2 bg-transparent border-0"
          >
            <FaTimes size={16} color="#666" />
          </button>
          {!isRegistered && (
            <div className="text-center mb-4">
              <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#00C70D] to-[#005AD0] bg-clip-text text-transparent">
                Member Sign Up
              </h3>
              <p className="text-gray-500 text-xs md:text-sm">
                Fill in your details to sign up as a Member
              </p>
            </div>
          )}
          {isRegistered ? (
            <div className="text-center py-6">
              <h2 className="text-lg md:text-xl font-bold text-green-600">
                Registration Successful
              </h2>
              <svg
                className="w-10 h-10 mx-auto text-green-600 mt-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          ) : (
            <>
              <Formik
                initialValues={initialSignupValues}
                validationSchema={signupSchemas[currentStep]}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                  if (currentStep < steps.length - 1) {
                    setCurrentStep((prev) => prev + 1);
                    setSubmitting(false);
                  } else {
                    try {
                      const formData = new FormData();
                      formData.append("name", values.name);
                      formData.append("dob", values.dob);
                      formData.append("gender", values.gender);
                      formData.append("email", values.email);
                      formData.append("mobile", values.mobile);
                      formData.append("bloodGroup", values.bloodGroup);
                      formData.append("address_line", values.resAddress_line);
                      formData.append("country", values.resCountry);
                      formData.append("state", values.resState);
                      formData.append("city", values.resCity);
                      formData.append("district", values.resDistrict);
                      formData.append("pincode", values.resPincode);
                      formData.append(
                        "correAddress_line",
                        values.correAddress_line,
                      );
                      formData.append("correCountry", values.correCountry);
                      formData.append("correState", values.correState);
                      formData.append("correCity", values.correCity);
                      formData.append("correDistrict", values.correDistrict);
                      formData.append("correPincode", values.correPincode);
                      if (values.referralCode) {
                        formData.append("referralCode", values.referralCode);
                      }
                      formData.append("profilePhoto", values.profilePhoto);

                      await dispatch(registerUser(formData)).unwrap();
                      setIsRegistered(true);
                      resetForm();
                      setTimeout(() => {
                        onHide();
                        toast.success(
                          "Registration successful! Please sign in.",
                        );
                      }, 2000);
                    } catch (error) {
                      console.error("Registration error:", error);
                      toast.error(
                        error.response?.data?.message || "Registration failed",
                      );
                    } finally {
                      setSubmitting(false);
                    }
                  }
                }}
              >
                {({ isSubmitting, setFieldValue, values }) => (
                  <Form className="flex flex-col gap-3">
                    {currentStep === 0 && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-10 gap-3">
                          {/* Profile */}
                          <div className="md:col-span-2 flex justify-center">
                            <div className="flex flex-col items-center">
                              <div
                                className="w-24 h-24 md:w-28 md:h-28 border border-gray-300 rounded-md overflow-hidden cursor-pointer"
                                onClick={() =>
                                  document
                                    .getElementById("profilePhoto")
                                    .click()
                                }
                              >
                                {previewImage ? (
                                  <img
                                    src={previewImage}
                                    alt="Profile Preview"
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                                    <span className="text-[11px] text-gray-500 text-center px-2">
                                      Upload Profile
                                    </span>
                                  </div>
                                )}
                              </div>

                              <input
                                type="file"
                                id="profilePhoto"
                                className="hidden"
                                accept="image/*"
                                onChange={(event) => {
                                  const file = event.target.files[0];
                                  setFieldValue("profilePhoto", file);

                                  if (file) {
                                    setPreviewImage(URL.createObjectURL(file));
                                  } else {
                                    setPreviewImage(null);
                                  }
                                }}
                              />

                              <ErrorMessage
                                name="profilePhoto"
                                component="div"
                                className="text-red-500 text-[10px] mt-1 text-center"
                              />
                            </div>
                          </div>

                          {/* Basic Details */}
                          <div className="md:col-span-8">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Full Name
                                  <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="name"
                                  placeholder="Enter Full Name"
                                  className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                                />
                                <ErrorMessage
                                  name="name"
                                  component="div"
                                  className="text-red-500 text-[10px] mt-0.5"
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Phone Number
                                  <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="mobile"
                                  placeholder="Enter Phone Number"
                                  className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                                />
                                <ErrorMessage
                                  name="mobile"
                                  component="div"
                                  className="text-red-500 text-[10px] mt-0.5"
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Email<span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="email"
                                  name="email"
                                  placeholder="Enter Email"
                                  className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                                />
                                <ErrorMessage
                                  name="email"
                                  component="div"
                                  className="text-red-500 text-[10px] mt-0.5"
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Blood Group
                                  <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  as="select"
                                  name="bloodGroup"
                                  className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                                >
                                  <option value="">Select Blood Group</option>
                                  <option value="A+">A+</option>
                                  <option value="A-">A-</option>
                                  <option value="B+">B+</option>
                                  <option value="B-">B-</option>
                                  <option value="AB+">AB+</option>
                                  <option value="AB-">AB-</option>
                                  <option value="O+">O+</option>
                                  <option value="O-">O-</option>
                                </Field>
                                <ErrorMessage
                                  name="bloodGroup"
                                  component="div"
                                  className="text-red-500 text-[10px] mt-0.5"
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Date of Birth
                                  <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="date"
                                  name="dob"
                                  max={maxDate18.toISOString().split("T")[0]}
                                  className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                                />
                                <ErrorMessage
                                  name="dob"
                                  component="div"
                                  className="text-red-500 text-[10px] mt-0.5"
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium mb-1">
                                  Gender<span className="text-red-500">*</span>
                                </label>
                                <Field
                                  as="select"
                                  name="gender"
                                  className="modern-input w-full p-2 text-xs border border-gray-300 bg-gray-100 rounded-md"
                                >
                                  <option value="">Select Gender</option>
                                  <option value="male">Male</option>
                                  <option value="female">Female</option>
                                  <option value="other">Other</option>
                                </Field>
                                <ErrorMessage
                                  name="gender"
                                  component="div"
                                  className="text-red-500 text-[10px] mt-0.5"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-2">
                          <h3 className="text-sm font-semibold mb-2">
                            Residential Address{" "}
                            <span className="text-red-500">*</span>
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                Street Address{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="resAddress_line"
                                placeholder="Enter Street Address"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="resAddress_line"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                City <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="resCity"
                                placeholder="Enter City"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="resCity"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                District <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="resDistrict"
                                placeholder="Enter District"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="resDistrict"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                Pincode <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="resPincode"
                                placeholder="Enter Pincode"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="resPincode"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                Country <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="resCountry"
                                placeholder="Enter Country"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="resCountry"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                State <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="resState"
                                placeholder="Enter State"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="resState"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                          </div>
                        </div>
                        {/* Checkbox */}
                        <div className="mt-1">
                          <label className="flex items-center text-sm font-medium text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={useSameAddress}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setUseSameAddress(checked);
                                if (checked) {
                                  setFieldValue(
                                    "correAddress_line",
                                    values.resAddress_line || "",
                                  );
                                  setFieldValue(
                                    "correCountry",
                                    values.resCountry || "",
                                  );
                                  setFieldValue(
                                    "correState",
                                    values.resState || "",
                                  );
                                  setFieldValue(
                                    "correCity",
                                    values.resCity || "",
                                  );
                                  setFieldValue(
                                    "correDistrict",
                                    values.resDistrict || "",
                                  );
                                  setFieldValue(
                                    "correPincode",
                                    values.resPincode || "",
                                  );
                                } else {
                                  // Optional: clear correspondence fields when unchecked
                                  setFieldValue("correAddress_line", "");
                                  setFieldValue("correCountry", "");
                                  setFieldValue("correState", "");
                                  setFieldValue("correCity", "");
                                  setFieldValue("correDistrict", "");
                                  setFieldValue("correPincode", "");
                                }
                              }}
                              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded mr-2"
                            />
                            <span>
                              Same as Residential Address (for Correspondence /
                              Office Address)
                            </span>
                          </label>
                        </div>
                        <div className="mt-1">
                          <h3 className="text-sm font-semibold mb-2">
                            Correspondence / Office Address{" "}
                            <span className="text-red-500">*</span>
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                Street Address{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="correAddress_line"
                                placeholder="Enter Street Address"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="correAddress_line"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                City <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="correCity"
                                placeholder="Enter City"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="correCity"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                District <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="correDistrict"
                                placeholder="Enter District"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="correDistrict"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                Pincode <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="correPincode"
                                placeholder="Enter Pincode"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="correPincode"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                Country <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="correCountry"
                                placeholder="Enter Country"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="correCountry"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">
                                State <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="correState"
                                placeholder="Enter State"
                                className="modern-input w-full px-3 py-2 text-xs border border-gray-300 bg-gray-100 rounded-md focus:ring-1 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="correState"
                                component="div"
                                className="text-red-500 text-[10px] mt-0.5"
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {currentStep === 1 && (
                      <>
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm md:text-base font-semibold">
                              Referral Code
                            </label>
                            <Field
                              type="text"
                              name="referralCode"
                              placeholder="Enter Referral Code"
                              className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                              onChange={(e) => {
                                setFieldValue("referralCode", e.target.value);
                                debouncedFetchReferredUserData(e.target.value);
                              }}
                            />
                            <ErrorMessage
                              name="referralCode"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </div>
                        {referredUserData ? (
                          <>
                            <h2 className="text-lg md:text-xl font-medium">
                              Upline Information
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm md:text-base font-semibold">
                                  Full Name
                                </label>
                                <input
                                  type="text"
                                  value={referredUserData.name || ""}
                                  readOnly
                                  className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                                />
                              </div>
                              <div>
                                <label className="block text-sm md:text-base font-semibold">
                                  Phone Number
                                </label>
                                <input
                                  type="text"
                                  value={
                                    referredUserData.mobile ||
                                    referredUserData.phone ||
                                    ""
                                  }
                                  readOnly
                                  className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100"
                                />
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            {values.referralCode && (
                              <div className="text-red-500 text-xs mt-1">
                                Invalid Referral Code
                              </div>
                            )}
                          </>
                        )}
                        <div className="mt-4">
                          <h2 className="text-lg md:text-xl font-medium">
                            Terms and Conditions
                          </h2>
                          <div className="max-h-40 overflow-y-auto border border-gray-300 p-3 rounded-md bg-gray-50 text-sm">
                            <p>
                              By enrolling as a member and network marketing
                              partner of LifeOnPlus, you agree to the following
                              terms:
                            </p>
                            <ol className="list-decimal ml-4 mt-2">
                              <li>
                                <strong>Eligibility:</strong> You must be 18+
                                years of age with valid KYC documents.
                              </li>
                              <li>
                                <strong>Referral Income:</strong> Income is
                                earned only upon successful sale or onboarding
                                as per the business plan and is subject to
                                applicable deductions (TDS, service fee, GST).
                              </li>
                              <li>
                                <strong>Commission Withdrawal:</strong>
                                <ul className="list-disc ml-4">
                                  <li>Minimum withdrawal amount: ₹200</li>
                                  <li>
                                    TDS of 2% (20% if PAN not provided), service
                                    charge of 10% + GST (18%)
                                  </li>
                                  <li>
                                    Wallet retention policy applies (e.g., 40%
                                    wallet holding)
                                  </li>
                                </ul>
                              </li>
                              <li>
                                <strong>Compliance:</strong> You are responsible
                                for ensuring ethical promotion of LifeOnPlus
                                services and not making false claims.
                              </li>
                              <li>
                                <strong>Termination:</strong> LifeOnPlus
                                reserves the right to terminate a member’s
                                account in case of fraudulent activity,
                                misrepresentation, or breach of company
                                policies.
                              </li>
                              <li>
                                <strong>Data Usage:</strong> Your personal and
                                banking data will be securely stored and only
                                used for internal business and payout purposes.
                              </li>
                              <li>
                                <strong>Refund Policy:</strong> Refunds
                                applicable only if 10x business is done within
                                120 hours (for specific ranks).
                              </li>
                              <li>
                                <strong>Non-Transferable:</strong> Your
                                membership is personal and cannot be transferred
                                or sold.
                              </li>
                              <li>
                                <strong>
                                  Business Opportunity Disclaimer:
                                </strong>{" "}
                                Earnings depend on your effort, sales
                                performance, and team activity. LifeOnPlus does
                                not guarantee fixed returns.
                              </li>
                              <li>
                                <strong>Code of Conduct:</strong> As a
                                representative of LifeOnPlus, you are expected
                                to maintain professionalism and comply with all
                                legal norms.
                              </li>
                            </ol>
                          </div>
                          <div className="mt-2 flex items-center">
                            <Field
                              type="checkbox"
                              name="termsAccepted"
                              className="h-4 w-4 text-[#00C70D] border-gray-300 rounded cursor-pointer"
                            />
                            <label className="ml-2 text-sm">
                              I accept the Terms and Conditions
                            </label>
                          </div>
                          <ErrorMessage
                            name="termsAccepted"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                        <div className="mt-4">
                          <h2 className="text-lg md:text-xl font-medium">
                            Declaration
                          </h2>
                          <div className="border border-gray-300 p-3 rounded-md bg-gray-50 text-sm">
                            <p>
                              I, {values.name || "_________"}, declare that the
                              information provided is true and correct. I have
                              read and understood the Terms & Conditions of
                              LifeOnPlus and agree to abide by them.
                            </p>
                          </div>
                          <div className="mt-2 flex items-center">
                            <Field
                              type="checkbox"
                              name="declarationAccepted"
                              className="h-4 w-4 text-[#00C70D] border-gray-300 rounded cursor-pointer"
                            />
                            <label className="ml-2 text-sm">
                              I accept the Declaration
                            </label>
                          </div>
                          <ErrorMessage
                            name="declarationAccepted"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>
                      </>
                    )}
                    <div className="mt-6 flex justify-center gap-3">
                      {currentStep > 0 && (
                        <button
                          type="button"
                          className="w-28 md:w-36 py-2 bg-gray-300 text-gray-800 font-semibold text-sm md:text-base rounded-md shadow-md hover:bg-gray-400"
                          onClick={() => setCurrentStep((prev) => prev - 1)}
                        >
                          Back
                        </button>
                      )}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-28 md:w-36 py-2 bg-[#00C70D] text-white font-semibold text-sm md:text-base rounded-md shadow-md flex items-center justify-center ${
                          isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                        }`}
                      >
                        {isSubmitting ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 md:h-5 md:w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Loading...
                          </>
                        ) : currentStep < steps.length - 1 ? (
                          "Continue"
                        ) : (
                          "Register"
                        )}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default MemberSignUp;
